import { inject, Injectable } from '@angular/core';
import { firstValueFrom, map } from 'rxjs';
import { filterSuccessResult } from '@ngneat/query';
import { ActivityEntityName, MetadataEntityFieldData, resolveFieldValueSetKind } from '@jms/model';
import { EntityDataService } from '@workbuddy/services/entities';
import { resolveEntityFieldPickerSpec, resolveEntityFieldStaticOptions } from '../../providers/entity.field.value.set';
import { KanbanValueSetOption } from './kanban.buckets';

/**
 * Loads a kanban column field's complete value set.
 *
 * Columns are the field's whole set, not the values the current page happens to hold, so this answers
 * "every value this field can take" once per field and the board keeps a stable column set as the user
 * pages. `null` means there is no enumerable set and the caller must fall back to data-driven columns.
 *
 * Results are cached by field path for the board's lifetime: a status list does not change while an
 * operator works a board, and re-fetching on every re-bucket would thrash the picker endpoint.
 */
@Injectable()
export class KanbanValueSetService {

  private readonly entityService = inject(EntityDataService);
  private readonly cache = new Map<string, Promise<KanbanValueSetOption[] | null>>();

  public load(field: MetadataEntityFieldData | null | undefined, entity?: ActivityEntityName | null): Promise<KanbanValueSetOption[] | null> {

    const path = field?.path ?? field?.name;
    if (!field || !path) return Promise.resolve(null);

    // The board and the view editor share this cache, so opening Edit View does not re-fetch the set
    // the board is already rendering from.
    const key = `${entity ?? ''}|${path}`;
    const cached = this.cache.get(key);
    if (cached) return cached;

    const pending = this.resolve(field, entity);
    this.cache.set(key, pending);
    return pending;
  }

  public clear(): void {

    this.cache.clear();
  }

  private resolve(field: MetadataEntityFieldData, entity?: ActivityEntityName | null): Promise<KanbanValueSetOption[] | null> {

    // An 'open' column path only reaches here from a saved view written before bounded columns were
    // enforced; it renders data-driven as a graceful fallback but is no longer a supported config.
    const kind = resolveFieldValueSetKind(field);
    if (kind === 'open') return Promise.resolve(null);

    if (kind === 'static') {
      const options = resolveEntityFieldStaticOptions(field);
      return Promise.resolve(options?.length ? options.map(option => toValueSetOption(option.id, option.label, option.color, null, option.data)) : null);
    }

    return this.loadPicker(field, entity);
  }

  private async loadPicker(field: MetadataEntityFieldData, entity?: ActivityEntityName | null): Promise<KanbanValueSetOption[] | null> {

    const spec = resolveEntityFieldPickerSpec(field, entity);
    if (!spec) return null;

    try {
      const items = await firstValueFrom(this.entityService
        .getPicker(spec.entity, spec.buildRequest())
        .result$
        .pipe(
          filterSuccessResult(),
          map(result => result.data?.items ?? []),
        ));

      if (!items.length) return null;

      return items.map((item: any) => toValueSetOption(pickerOptionKey(item), item.name ?? '', item.color ?? null, typeof item.sortOrder === 'number' ? item.sortOrder : null, item));
    } catch {
      // A failed lookup must not blank the board — the caller falls back to data-driven columns.
      return null;
    }
  }
}

/**
 * Key for a picker record.
 *
 * Must agree with the board's `getGroupKey` derivation for the row's embedded value, or a populated
 * column would render empty beside a stray duplicate. Both read `_id` first, which is the field the
 * WorkOrderStatus picker option and the embedded `workOrders.status` share.
 */
function pickerOptionKey(item: any): string {

  return item?._id ?? item?.id ?? item?.systemName ?? item?.name ?? '';
}

function toValueSetOption(key: string | number, name: string, color: string | null | undefined, sortOrder: number | null, data: any): KanbanValueSetOption {

  return { key: String(key), name, color: color ?? null, sortOrder, data };
}
